import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, withModifiers as _withModifiers, withKeys as _withKeys, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, normalizeStyle as _normalizeStyle, Transition as _Transition } from "vue"

const _hoisted_1 = ["aria-valuenow", "aria-valuemin", "aria-valuemax"]
const _hoisted_2 = { class: "flex items-center gap-2 border-b border-gray-200 px-4 py-3" }
const _hoisted_3 = { class: "!hidden sm:!block" }
const _hoisted_4 = {
  key: 0,
  class: "max-w-[85%] self-end whitespace-pre-wrap rounded-2xl bg-gray-100 px-3 py-2",
  "data-cy": "clerk-ai-user-message"
}
const _hoisted_5 = {
  key: 1,
  class: "w-full",
  "data-cy": "clerk-ai-assistant-message"
}
const _hoisted_6 = {
  key: 0,
  class: "flex items-center gap-2 text-gray-500"
}
const _hoisted_7 = {
  key: 1,
  class: "text-red-700"
}
const _hoisted_8 = {
  key: 0,
  class: "mt-2 overflow-hidden rounded-lg border border-gray-200",
  "data-cy": "clerk-ai-table"
}
const _hoisted_9 = {
  key: 0,
  class: "m-0 border-t border-gray-200 px-2 py-1.5 text-xs text-gray-500"
}
const _hoisted_10 = {
  class: "mt-1 flex items-center gap-0.5 text-gray-500",
  "data-cy": "clerk-ai-message-actions"
}
const _hoisted_11 = {
  key: 0,
  class: "m-0 mb-4 text-center text-lg font-medium text-gray-700",
  "data-cy": "clerk-ai-greeting"
}
const _hoisted_12 = {
  key: 1,
  class: "mb-4 grid grid-cols-2 gap-2",
  "data-cy": "clerk-ai-starter-prompts"
}
const _hoisted_13 = { class: "flex flex-col items-start gap-0.5 whitespace-normal normal-case" }
const _hoisted_14 = { class: "text-xs font-semibold text-primary" }
const _hoisted_15 = {
  class: "text-sm font-normal text-gray-700",
  "data-cy": "clerk-ai-starter-prompt-question"
}
const _hoisted_16 = {
  class: "flex min-h-[28px] items-center justify-end gap-2",
  "data-cy": "clerk-ai-composer-toolbar"
}
const _hoisted_17 = { class: "text-xs text-gray-500" }
const _hoisted_18 = {
  key: 2,
  class: "m-0 mt-2 flex items-start justify-center gap-1 text-[11px] text-gray-500",
  "data-cy": "clerk-ai-privacy-note"
}

import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useClerkAiStore } from '@/stores/insights/useClerkAiStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import {
  clampPanelWidth,
  csvColumnMap,
  csvFilename,
  formatCell,
  parseSummary,
  toPlainText,
} from '@/utils/clerkAiHelpers';
import { useExportUtilities } from '@/composables/useExportUtilities';
import type { ClerkAiAssistantMessage } from '@/models/clerkAiTypes';
import {
  CLERK_AI_MAX_WIDTH,
  CLERK_AI_MIN_WIDTH,
  CLERK_AI_RESIZE_STEP,
  CLERK_AI_STARTER_PROMPTS,
} from '@/constants/clerkAiConstants';
import LlIcon from '@/components/LlIcon.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlTextarea from '@/components/LlTextarea.vue';

const MAX_ROWS = 200;


export default /*@__PURE__*/_defineComponent({
  __name: 'ClerkAiPanel',
  setup(__props) {

const clerkAi = useClerkAiStore();
const { isOpen, messages, sending, isEmpty, width, expanded } =
  storeToRefs(clerkAi);
const { handleError } = useSnackbarHandler();

const draft = ref('');
const chatRef = ref<HTMLElement | null>(null);
const inputRef = ref<{ focus: () => void } | null>(null);

const viewportWidth = ref(window.innerWidth);
const resizing = ref(false);

const panelWidth = computed(() =>
  clampPanelWidth(width.value, viewportWidth.value),
);

// Width eases between sizes on expand/restore, but follows a drag directly.
const panelStyle = computed(() => ({
  '--clerk-ai-width': expanded.value ? '100%' : `${panelWidth.value}px`,
  transition: resizing.value
    ? 'transform 200ms ease-out'
    : 'transform 200ms ease-out, width 200ms ease-out',
}));

const onWindowResize = () => {
  viewportWidth.value = window.innerWidth;
};

onMounted(() => window.addEventListener('resize', onWindowResize));

const startResize = (event: PointerEvent) => {
  event.preventDefault();
  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  resizing.value = true;
};

const resize = (event: PointerEvent) => {
  if (!resizing.value) return;
  clerkAi.setWidth(viewportWidth.value - event.clientX, viewportWidth.value);
};

const endResize = (event: PointerEvent) => {
  if (!resizing.value) return;
  resizing.value = false;
  clerkAi.persistWidth();
  // On pointercancel the browser may already have dropped the capture; releasing it again throws.
  const handle = event.currentTarget as HTMLElement;
  if (handle.hasPointerCapture(event.pointerId)) {
    handle.releasePointerCapture(event.pointerId);
  }
};

const resizeBy = (delta: number) => {
  clerkAi.setWidth(panelWidth.value + delta, viewportWidth.value);
  clerkAi.persistWidth();
};

onBeforeUnmount(() => {
  clearTimeout(copiedTimer);
  window.removeEventListener('resize', onWindowResize);
});

const canSend = computed(() => !sending.value && draft.value.trim().length > 0);

const scrollToBottom = () =>
  nextTick(() => {
    if (chatRef.value) chatRef.value.scrollTop = chatRef.value.scrollHeight;
  });

watch(isOpen, (open) => {
  if (!open) return;
  scrollToBottom().then(() => inputRef.value?.focus());
});

const messageProgress = computed(() =>
  messages.value
    .map((m) => (m.role === 'assistant' ? `${m.id}:${m.status}` : m.id))
    .join(','),
);

watch(messageProgress, scrollToBottom);

const ask = async (question: string) => {
  try {
    await clerkAi.send(question);
  } catch (error) {
    handleError(error);
  }
};

const submit = () => {
  if (!canSend.value) return;
  const question = draft.value;
  draft.value = '';
  ask(question);
};

const { exportCSV } = useExportUtilities();
const copiedId = ref<number | null>(null);
let copiedTimer: ReturnType<typeof setTimeout> | undefined;

const copyAnswer = async (message: ClerkAiAssistantMessage) => {
  try {
    await navigator.clipboard.writeText(
      toPlainText(message.summary, message.table),
    );
    copiedId.value = message.id;
    clearTimeout(copiedTimer);
    copiedTimer = setTimeout(() => {
      copiedId.value = null;
    }, 2000);
  } catch {
    // The browser's own message ("Write permission denied") means nothing to a user.
    handleError(
      "Your browser didn't allow copying. Select the answer and copy it instead.",
    );
  }
};

const downloadCsv = async (message: ClerkAiAssistantMessage) => {
  if (!message.table) return;
  try {
    await exportCSV(message.table.rows, csvColumnMap(message.table), {
      filename: csvFilename(message.question),
    });
  } catch (error) {
    handleError(error);
  }
};

const retry = async (id: number) => {
  try {
    await clerkAi.retry(id);
  } catch (error) {
    handleError(error);
  }
};

return (_ctx: any,_cache: any) => {
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_table = _resolveComponent("v-table")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createBlock(_Transition, {
    "enter-active-class": "transition-transform duration-200 ease-out",
    "leave-active-class": "transition-transform duration-200 ease-in",
    "enter-from-class": "[transform:translateX(100%)]",
    "leave-to-class": "[transform:translateX(100%)]"
  }, {
    default: _withCtx(() => [
      (_unref(isOpen))
        ? (_openBlock(), _createElementBlock("aside", {
            key: 0,
            class: "fixed top-0 right-0 bottom-0 z-[1500] flex w-full flex-col border-l border-gray-200 bg-white shadow-2xl sm:w-[var(--clerk-ai-width)]",
            style: _normalizeStyle(panelStyle.value),
            "aria-label": "ClerkAI",
            "data-cy": "clerk-ai-panel"
          }, [
            (!_unref(expanded))
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: _normalizeClass(["absolute inset-y-0 -left-1 z-10 w-2 cursor-col-resize hover:bg-gray-200 focus:bg-gray-200 focus:outline-none !hidden sm:!block", { 'bg-gray-200': resizing.value }]),
                  role: "separator",
                  "aria-orientation": "vertical",
                  "aria-label": "Resize ClerkAI",
                  "aria-valuenow": panelWidth.value,
                  "aria-valuemin": _unref(CLERK_AI_MIN_WIDTH),
                  "aria-valuemax": _unref(CLERK_AI_MAX_WIDTH),
                  tabindex: "0",
                  "data-cy": "clerk-ai-resize",
                  onPointerdown: startResize,
                  onPointermove: resize,
                  onPointerup: endResize,
                  onPointercancel: endResize,
                  onKeydown: [
                    _cache[0] || (_cache[0] = _withKeys(_withModifiers(($event: any) => (resizeBy(_unref(CLERK_AI_RESIZE_STEP))), ["prevent"]), ["left"])),
                    _cache[1] || (_cache[1] = _withKeys(_withModifiers(($event: any) => (resizeBy(-_unref(CLERK_AI_RESIZE_STEP))), ["prevent"]), ["right"]))
                  ]
                }, null, 42, _hoisted_1))
              : _createCommentVNode("", true),
            _createElementVNode("header", _hoisted_2, [
              _createVNode(LlIcon, {
                icon: "mdi-message-question-outline",
                color: "primary",
                size: "small"
              }),
              _cache[8] || (_cache[8] = _createElementVNode("h2", { class: "m-0 flex-1 text-base font-semibold" }, "ClerkAI", -1)),
              _createVNode(LlSecondaryButton, {
                size: "small",
                variant: "text",
                "prepend-icon": "mdi-plus",
                disabled: _unref(isEmpty),
                "data-cy": "clerk-ai-new-chat",
                onClick: _cache[2] || (_cache[2] = ($event: any) => (_unref(clerkAi).newChat()))
              }, {
                default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                  _createTextVNode(" New ", -1)
                ]))]),
                _: 1
              }, 8, ["disabled"]),
              _createElementVNode("div", _hoisted_3, [
                _createVNode(LlSecondaryButton, {
                  icon: _unref(expanded) ? 'mdi-arrow-collapse' : 'mdi-arrow-expand',
                  variant: "text",
                  size: "small",
                  "aria-label": _unref(expanded) ? 'Restore ClerkAI size' : 'Expand ClerkAI',
                  title: _unref(expanded) ? 'Restore size' : 'Expand',
                  "aria-pressed": _unref(expanded),
                  "data-cy": "clerk-ai-expand",
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (_unref(clerkAi).toggleExpanded()))
                }, null, 8, ["icon", "aria-label", "title", "aria-pressed"])
              ]),
              _createVNode(LlSecondaryButton, {
                icon: "mdi-close",
                variant: "text",
                size: "small",
                "aria-label": "Close ClerkAI",
                "data-cy": "clerk-ai-close",
                onClick: _cache[4] || (_cache[4] = ($event: any) => (_unref(clerkAi).close()))
              })
            ]),
            _cache[15] || (_cache[15] = _createElementVNode("p", { class: "m-0 bg-blue-50 px-4 py-2 text-xs text-blue-700" }, " This is a pre-release feature enabled for evaluation in your account. It may not be available in your current plan in the future. ", -1)),
            _createElementVNode("div", {
              ref_key: "chatRef",
              ref: chatRef,
              class: _normalizeClass(["flex flex-col overflow-y-auto text-sm", { 'flex-1 p-4': !_unref(isEmpty) }]),
              "aria-live": "polite",
              "data-cy": "clerk-ai-messages"
            }, [
              _createElementVNode("div", {
                class: _normalizeClass(["flex w-full flex-col gap-4", { 'mx-auto max-w-3xl': _unref(expanded) }])
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(messages), (message) => {
                  return (_openBlock(), _createElementBlock(_Fragment, {
                    key: message.id
                  }, [
                    (message.role === 'user')
                      ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(message.text), 1))
                      : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                          (message.status === 'pending')
                            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                _createVNode(_component_v_progress_circular, {
                                  indeterminate: "",
                                  size: "16",
                                  width: "2",
                                  color: "primary"
                                }),
                                _cache[9] || (_cache[9] = _createTextVNode(" Thinking… ", -1))
                              ]))
                            : (message.status === 'error')
                              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                  _cache[11] || (_cache[11] = _createElementVNode("p", { class: "m-0 mb-2" }, "ClerkAI couldn't finish this answer.", -1)),
                                  _createVNode(LlSecondaryButton, {
                                    size: "small",
                                    disabled: _unref(sending),
                                    "data-cy": "clerk-ai-retry",
                                    onClick: ($event: any) => (retry(message.id))
                                  }, {
                                    default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                                      _createTextVNode(" Try again ", -1)
                                    ]))]),
                                    _: 1
                                  }, 8, ["disabled", "onClick"])
                                ]))
                              : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(parseSummary)(message.summary), (block, index) => {
                                    return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
                                      (block.type === 'list')
                                        ? (_openBlock(), _createBlock(_resolveDynamicComponent(block.ordered ? 'ol' : 'ul'), {
                                            key: 0,
                                            start: block.ordered ? block.start : undefined,
                                            class: _normalizeClass(["my-2 ml-0 space-y-1 pl-6", block.ordered ? 'list-decimal' : 'list-disc'])
                                          }, {
                                            default: _withCtx(() => [
                                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(block.items, (item, itemIndex) => {
                                                return (_openBlock(), _createElementBlock("li", { key: itemIndex }, [
                                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(item, (segment, segmentIndex) => {
                                                    return (_openBlock(), _createBlock(_resolveDynamicComponent(segment.bold ? 'strong' : 'span'), { key: segmentIndex }, {
                                                      default: _withCtx(() => [
                                                        _createTextVNode(_toDisplayString(segment.text), 1)
                                                      ]),
                                                      _: 2
                                                    }, 1024))
                                                  }), 128))
                                                ]))
                                              }), 128))
                                            ]),
                                            _: 2
                                          }, 1032, ["start", "class"]))
                                        : (_openBlock(), _createElementBlock("p", {
                                            key: 1,
                                            class: _normalizeClass(
                      block.type === 'heading'
                        ? 'mb-1 mt-3 font-semibold'
                        : 'my-1'
                    )
                                          }, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(block.segments, (segment, segmentIndex) => {
                                              return (_openBlock(), _createBlock(_resolveDynamicComponent(segment.bold ? 'strong' : 'span'), { key: segmentIndex }, {
                                                default: _withCtx(() => [
                                                  _createTextVNode(_toDisplayString(segment.text), 1)
                                                ]),
                                                _: 2
                                              }, 1024))
                                            }), 128))
                                          ], 2))
                                    ], 64))
                                  }), 128)),
                                  (message.table)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                        _createVNode(_component_v_table, {
                                          density: "compact",
                                          "fixed-header": "",
                                          height: message.table.rows.length > 8 ? 320 : undefined,
                                          class: "text-xs"
                                        }, {
                                          default: _withCtx(() => [
                                            _createElementVNode("thead", null, [
                                              _createElementVNode("tr", null, [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(message.table.columns, (column, columnIndex) => {
                                                  return (_openBlock(), _createElementBlock("th", {
                                                    key: columnIndex,
                                                    scope: "col",
                                                    class: "whitespace-nowrap"
                                                  }, _toDisplayString(column), 1))
                                                }), 128))
                                              ])
                                            ]),
                                            _createElementVNode("tbody", null, [
                                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(message.table.rows.slice(
                          0,
                          MAX_ROWS,
                        ), (row, rowIndex) => {
                                                return (_openBlock(), _createElementBlock("tr", { key: rowIndex }, [
                                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row, (cell, cellIndex) => {
                                                    return (_openBlock(), _createElementBlock("td", {
                                                      key: cellIndex,
                                                      class: _normalizeClass([
                            'whitespace-nowrap',
                            typeof cell === 'number'
                              ? 'text-right tabular-nums'
                              : '',
                          ])
                                                    }, _toDisplayString(_unref(formatCell)(cell)), 3))
                                                  }), 128))
                                                ]))
                                              }), 128))
                                            ])
                                          ]),
                                          _: 2
                                        }, 1032, ["height"]),
                                        (message.table.rows.length > MAX_ROWS)
                                          ? (_openBlock(), _createElementBlock("p", _hoisted_9, " Showing the first " + _toDisplayString(MAX_ROWS) + " of " + _toDisplayString(message.table.rows.length) + " rows. The download has them all. ", 1))
                                          : _createCommentVNode("", true)
                                      ]))
                                    : _createCommentVNode("", true),
                                  _createElementVNode("div", _hoisted_10, [
                                    _createVNode(LlSecondaryButton, {
                                      icon: 
                      copiedId.value === message.id ? 'mdi-check' : 'mdi-content-copy'
                    ,
                                      variant: "text",
                                      size: "small",
                                      "aria-label": 
                      copiedId.value === message.id ? 'Copied' : 'Copy answer'
                    ,
                                      title: copiedId.value === message.id ? 'Copied' : 'Copy answer',
                                      "data-cy": "clerk-ai-copy",
                                      onClick: ($event: any) => (copyAnswer(message))
                                    }, null, 8, ["icon", "aria-label", "title", "onClick"]),
                                    (message.table)
                                      ? (_openBlock(), _createBlock(LlSecondaryButton, {
                                          key: 0,
                                          icon: "mdi-download",
                                          variant: "text",
                                          size: "small",
                                          "aria-label": "Download as CSV",
                                          title: "Download as CSV",
                                          "data-cy": "clerk-ai-download",
                                          onClick: ($event: any) => (downloadCsv(message))
                                        }, null, 8, ["onClick"]))
                                      : _createCommentVNode("", true)
                                  ])
                                ], 64))
                        ]))
                  ], 64))
                }), 128))
              ], 2)
            ], 2),
            _createElementVNode("div", {
              class: _normalizeClass([
          _unref(isEmpty) ? 'my-auto px-6 pb-16' : 'px-3 pt-2 pb-20',
          { 'mx-auto w-full max-w-3xl': _unref(expanded) },
        ]),
              "data-cy": "clerk-ai-composer"
            }, [
              (_unref(isEmpty))
                ? (_openBlock(), _createElementBlock("p", _hoisted_11, " How can I help? "))
                : _createCommentVNode("", true),
              (_unref(isEmpty))
                ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(CLERK_AI_STARTER_PROMPTS), (prompt) => {
                      return (_openBlock(), _createBlock(LlSecondaryButton, {
                        key: prompt.question,
                        variant: "outlined",
                        class: "!h-auto !justify-start !rounded-2xl !border-gray-200 !px-3 !py-2 !text-left !tracking-normal",
                        disabled: _unref(sending),
                        "data-cy": "clerk-ai-starter-prompt",
                        onClick: ($event: any) => (ask(prompt.question))
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("span", _hoisted_13, [
                            _createElementVNode("span", _hoisted_14, _toDisplayString(prompt.topic), 1),
                            _createElementVNode("span", _hoisted_15, _toDisplayString(prompt.question), 1)
                          ])
                        ]),
                        _: 2
                      }, 1032, ["disabled", "onClick"]))
                    }), 128))
                  ]))
                : _createCommentVNode("", true),
              _createVNode(_component_v_form, {
                class: "rounded-3xl border border-gray-300 bg-white px-4 pt-2 pb-2 shadow-sm transition-colors focus-within:border-gray-400 focus-within:shadow-md [&_textarea]:!py-1 [&_textarea]:!text-sm [&_textarea]:!leading-5 [&_textarea]:!border-0 [&_textarea]:!shadow-none",
                onSubmit: _withModifiers(submit, ["prevent"]),
                onKeydown: [
                  _withKeys(_withModifiers(submit, ["exact","prevent"]), ["enter"]),
                  _cache[6] || (_cache[6] = _withKeys(($event: any) => (_unref(clerkAi).close()), ["esc"]))
                ]
              }, {
                default: _withCtx(() => [
                  _createVNode(LlTextarea, {
                    ref_key: "inputRef",
                    ref: inputRef,
                    modelValue: draft.value,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((draft).value = $event)),
                    variant: "plain",
                    rows: 1,
                    "max-rows": 6,
                    "auto-grow": "",
                    "no-resize": "",
                    placeholder: "Ask about hours, billing, collections…",
                    "aria-label": "Ask ClerkAI",
                    "data-cy": "clerk-ai-input"
                  }, null, 8, ["modelValue"]),
                  _createElementVNode("div", _hoisted_16, [
                    _createElementVNode("span", _hoisted_17, _toDisplayString(_unref(sending) ? 'ClerkAI is answering…' : 'Enter to send'), 1)
                  ])
                ]),
                _: 1
              }, 8, ["onKeydown"]),
              (_unref(isEmpty))
                ? (_openBlock(), _createElementBlock("p", _hoisted_18, [
                    _createVNode(LlIcon, {
                      icon: "mdi-shield-lock-outline",
                      size: "x-small",
                      class: "mt-0.5"
                    }),
                    _cache[12] || (_cache[12] = _createTextVNode(" AI can make mistakes; verify before acting. ", -1)),
                    _cache[13] || (_cache[13] = _createElementVNode("br", null, null, -1)),
                    _cache[14] || (_cache[14] = _createTextVNode(" We don’t train on your data. ", -1))
                  ]))
                : _createCommentVNode("", true)
            ], 2)
          ], 4))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})